import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Input, Select, Table, Spin,
  Typography, Button, Tooltip,
} from 'antd';
import { SearchOutlined, GlobalOutlined, ArrowUpOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { backendURL, IS_PUBLIC } from '../Globals';

const { Text } = Typography;

const DOAJ_API    = 'https://doaj.org/api/v3/search/journals';
const DOAJ_PROXY  = backendURL + '/doaj';
// Siempre usar el proxy backend (lee de DB local, funciona para todos incluido OCLC)
const DOAJ_BASE   = DOAJ_PROXY;
const BASE_FILTER = 'bibjson.apc.has_apc:false';
const PAGE_SIZE   = 20;

const Q_COLOR      = { Q1: '#27ae60', Q2: '#f1c40f', Q3: '#e67e22', Q4: '#e74c3c' };
const Q_TEXT_COLOR = { Q1: '#fff',    Q2: '#7a5a00', Q3: '#fff',    Q4: '#fff'    };
const CHART_COLORS = [
  '#5470c6','#91cc75','#fac858','#ee6666','#73c0de',
  '#3ba272','#fc8452','#9a60b4','#ea7ccc','#b8860b',
];

// ---------- helpers ISSN ---------------------------------------------------

const normalizeISSN = (raw) => {
  if (!raw || raw === 'None') return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{3}[\dXx]$/.test(s)) return s.toUpperCase();
  const d = s.replace('-', '');
  if (d.length === 8 && /^\d+$/.test(d.slice(0,7)))
    return d.slice(0, 4) + '-' + d.slice(4).toUpperCase();
  return null;
};

const extractISSNs = (journal) => {
  const b = journal.bibjson || {};
  const set = new Set();
  [b.eissn, b.pissn].forEach(i => { const n = normalizeISSN(i); if (n) set.add(n); });
  (b.identifier || []).forEach(id => { const n = normalizeISSN(id.id); if (n) set.add(n); });
  return [...set];
};

const displayISSN = (journal) => extractISSNs(journal)[0] || '\u2014';

// Primer resultado del mapa de índices para cualquier ISSN del journal
const getIndices = (journal, map) => {
  for (const issn of extractISSNs(journal)) {
    if (map[issn]) return map[issn];
  }
  return null;
};

// ---------- lookup backend -------------------------------------------------

const fetchLookup = async (doajResults) => {
  const all    = doajResults.flatMap(j => extractISSNs(j));
  const unique = [...new Set(all)];
  if (!unique.length) return {};
  try {
    const res = await fetch(backendURL + '/scimago/lookup?issns=' + unique.join(','));
    if (!res.ok) throw new Error('lookup ' + res.status);
    return await res.json();
  } catch (e) {
    console.error('lookup error:', e);
    return {};
  }
};

// Enriquecer cada journal con sus índices
const enrich = (results, map) =>
  results.map(j => ({ ...j, _idx: getIndices(j, map) }));

// ---------- chart helpers --------------------------------------------------

const Q_PIE_COLORS = {
  Q1: Q_COLOR.Q1, Q2: Q_COLOR.Q2, Q3: Q_COLOR.Q3, Q4: Q_COLOR.Q4,
  'Sin JCR': '#d9d9d9', 'Sin SJR': '#d9d9d9',
};

const makeDonut = (title, data) => ({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [{
    name: title, type: 'pie', radius: ['40%', '68%'], center: ['50%', '50%'],
    data: data.map(d => ({ name: d.name, value: d.value, itemStyle: { color: Q_PIE_COLORS[d.name] || '#d9d9d9' } })),
    label: { show: false },
    emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,.3)' } },
  }],
});

const makeAreaDonut = (title, data) => ({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  color: CHART_COLORS,
  series: [{
    name: title, type: 'pie', radius: ['38%', '62%'], center: ['50%', '50%'],
    data,
    label: { show: false },
    emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,.3)' } },
  }],
});


// ---------- QuartileBadge --------------------------------------------------

const QuartileBadge = ({ label, q }) => (
  <div style={{ fontSize: 11, lineHeight: 1.9, display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ color: '#aaa', minWidth: 70 }}>{label}:</span>
    {q
      ? <span style={{
          background: Q_COLOR[q], color: Q_TEXT_COLOR[q],
          fontWeight: 700, padding: '1px 7px', borderRadius: 3, fontSize: 11,
        }}>{q}</span>
      : <span style={{ color: '#bbb', fontSize: 11 }}>No indexada</span>
    }
  </div>
);

const Highlight = ({ text, query }) => {
  const escHtml = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  if (!query || !text) return <span dangerouslySetInnerHTML={{ __html: escHtml(text) }} />;
  const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${esc})`, 'gi'));
  const html = parts.map((p, i) =>
    i % 2 === 1
      ? `<span style="background-color:#e8b400">${escHtml(p)}</span>`
      : escHtml(p)
  ).join('');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

// ==========================================================================
// COMPONENT
// ==========================================================================

const DiamondComponent = () => {
  const [journals,         setJournals]         = useState([]);
  const [total,            setTotal]            = useState(0);
  const [loading,          setLoading]          = useState(false);
  const [statsLoading,     setStatsLoading]     = useState(true);

  const [searchTitle,      setSearchTitle]      = useState('');
  const [selPublisher,     setSelPublisher]     = useState(undefined);
  const [selSubject,       setSelSubject]       = useState(undefined);
  const [sjrqFilter,       setSjrqFilter]       = useState(null);
  const [page,             setPage]             = useState(1);

  const [areaChart,        setAreaChart]        = useState([]);
  const [jcrChart,         setJcrChart]         = useState([]);
  const [sjrChart,         setSjrChart]         = useState([]);
  const [filteredJournals, setFilteredJournals] = useState([]);
  const [filteredTotal,    setFilteredTotal]    = useState(0);
  const [filteredPage,     setFilteredPage]     = useState(1);
  const [resultTotal,      setResultTotal]      = useState(0);

  const [subjectOptions,   setSubjectOptions]   = useState([]);
  const [publisherOptions, setPublisherOptions] = useState([]);

  const debounceRef = useRef(null);

  // ---- Stats (desde DOAJ API) --------------------------------------------

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res  = await fetch(DOAJ_BASE + '/stats');
      if (!res.ok) throw new Error('DOAJ stats ' + res.status);
      const data = await res.json();

      setTotal(data.total || 0);

      // Áreas temáticas (top 10 desde BD completa)
      setAreaChart(data.areaCounts || []);
      // Cuartiles SJR/JCR: no disponibles para revistas Diamond (mayoría no indexadas)

    } catch(e) {
      console.error('fetchStats error:', e);
    }
    setStatsLoading(false);
  }, []);

  // ---- Opciones de filtros desde BD completa --------------------------------
  const fetchOptions = useCallback(async () => {
    try {
      const res = await fetch(DOAJ_BASE + '/options');
      if (!res.ok) return;
      const data = await res.json();
      setPublisherOptions((data.publishers || []).map(p => ({ label: p, value: p })));
      setSubjectOptions((data.subjects || []).map(s => ({ label: s, value: s })));
    } catch(e) {
      console.error('fetchOptions error:', e);
    }
  }, []);
  // ---- Journals (DOAJ, solo por título) ------------------------------------

  const fetchJournals = useCallback(async (p, title, pub, sub) => {
    setLoading(true);
    try {
      let url = DOAJ_BASE + '/search?pageSize=' + PAGE_SIZE + '&page=' + p;
      if (title) url += '&q='        + encodeURIComponent(title);
      if (pub)   url += '&publisher=' + encodeURIComponent(pub);
      if (sub)   url += '&subject='   + encodeURIComponent(sub);
      const res  = await fetch(url);
      const data = await res.json();
      const results = data.results || [];
      const map = await fetchLookup(results);
      const enriched = enrich(results, map);
      setJournals(enriched);
      if (p === 1) setResultTotal(data.total || 0);
    } catch(e) {
      console.error('fetchJournals error:', e);
    }
    setLoading(false);
  }, []);

  // ---- Backend (scimago diamond, filtros estructurados) -------------------

  const fetchFromBackend = useCallback(async (p, title, publisher, subject, sjrq, jcrq) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ diamond: '1', page: p, pageSize: PAGE_SIZE });
      if (title)     params.set('title', title);
      if (publisher) params.set('publisher', publisher);
      if (subject)   params.set('areas', subject);
      if (sjrq)      params.set('sjrq', sjrq);
      if (jcrq)      params.set('jcrq', jcrq);
      const res  = await fetch(backendURL + '/scimago?' + params);
      const data = await res.json();
      setFilteredJournals(data.results || []);
      setFilteredTotal(data.total || 0);
    } catch (e) {
      console.error('fetchFromBackend error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchJournals(1, '');
  }, [fetchStats, fetchOptions, fetchJournals]);

  const isBackend = !!sjrqFilter;

  const applyBackend = (pub, sub, sjrq) => {
    setFilteredPage(1);
    if (sjrq) {
      fetchFromBackend(1, searchTitle, pub, sub, sjrq, null);
    } else {
      fetchJournals(1, searchTitle, pub, sub);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setFilteredPage(1);

    // Registrar búsqueda (fire-and-forget)
    const q = [searchTitle, selPublisher, selSubject, sjrqFilter].filter(Boolean).join(' | ');
    if (q) {
      fetch(backendURL + '/usage/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module: 'diamante', query: q }),
      }).catch(() => {});
    }

    if (isBackend) {
      fetchFromBackend(1, searchTitle, selPublisher, selSubject, sjrqFilter, null);
    } else {
      fetchJournals(1, searchTitle, selPublisher, selSubject);
    }
  };
  const handleReset = () => {
    clearTimeout(debounceRef.current);
    setSearchTitle(''); setSelPublisher(undefined); setSelSubject(undefined);
    setSjrqFilter(null);
    setFilteredJournals([]); setFilteredTotal(0); setFilteredPage(1);
    setPage(1);
    fetchJournals(1, '', undefined, undefined);
  };
  const handlePageChange = p => {
    if (isBackend) {
      setFilteredPage(p);
      fetchFromBackend(p, searchTitle, selPublisher, selSubject, sjrqFilter, null);
    } else {
      setPage(p);
      fetchJournals(p, searchTitle, selPublisher, selSubject);
    }
  };

  const displayedJournals = useMemo(
    () => (sjrqFilter ? filteredJournals : journals),
    [journals, filteredJournals, sjrqFilter]
  );

  const visibleJournals = useMemo(() => {
    const q = searchTitle.trim().toLowerCase();
    if (!q || isBackend) return displayedJournals;
    return displayedJournals.filter(j =>
      (j.bibjson?.title || '').toLowerCase().includes(q) ||
      (j.bibjson?.publisher?.name || '').toLowerCase().includes(q) ||
      extractISSNs(j).some(issn => issn.toLowerCase().includes(q))
    );
  }, [displayedJournals, searchTitle, isBackend]);

  // ---- Columns ------------------------------------------------------------

  const columns = [
    {
      title: 'REVISTA', key: 'title',
      render: (_, r) => (
        <div className="jcard-main">
          <div className="jcard-title">
            {(() => {
              const doajId = r.id && r.id.startsWith('http') ? r.id.split('/').pop() : r.id;
              const doajUrl = ['https://', 'doaj.org/toc/'].join('') + doajId;
              return IS_PUBLIC
                ? <a href={doajUrl} target="_blank" rel="noopener noreferrer"><Highlight text={r.bibjson?.title || '\u2014'} query={searchTitle} /><ArrowUpOutlined className="jcard-link-icon" /></a>
                : <a href="#" onClick={e => { e.preventDefault(); window.open(doajUrl, '_blank', 'noopener,noreferrer'); }}><Highlight text={r.bibjson?.title || '\u2014'} query={searchTitle} /><ArrowUpOutlined className="jcard-link-icon" /></a>;
            })()}
          </div>
          {r.bibjson?.subject?.[0]?.term && (
            <div className="jcard-cats">
              <span className="jcard-cat-label">Área:</span>
              <span className="jcard-cat-val">{r.bibjson.subject[0].term}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'EDITORIAL', key: 'publisher', width: 220,
      render: (_, r) => {
        const name = r.bibjson?.publisher?.name || '\u2014';
        const needsTooltip = name.length > 45;
        return (
          <div className="jcard-pub-section">
            {needsTooltip
              ? <Tooltip title={name} mouseEnterDelay={0.2}>
                  <div className="jcard-pub-name" style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                    {name}
                  </div>
                </Tooltip>
              : <div className="jcard-pub-name">{name}</div>
            }
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              <QuartileBadge label="Cuartil SJR" q={r._idx?.sjrq} />
              <QuartileBadge label="Cuartil JCR" q={r._idx?.jcrq} />
            </div>
          </div>
        );
      },
    },
    {
      title: 'ISSN', key: 'issn', width: 120,
      render: (_, r) => (
        <div className="jcard-apc-section">
          <Tooltip title="ISSN" mouseEnterDelay={0.3}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#007398', cursor: 'default' }}>
              {displayISSN(r)}
            </span>
          </Tooltip>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, marginTop: 6 }}>
            <div className="jcard-apc-label">Sin</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#27ae60', fontFamily: "'Inter','Segoe UI',sans-serif" }}>APC</div>
          </div>
        </div>
      ),
    },
  ];

  // ---- Scimago columns (cuartil activo) -----------------------------------

  const scimagoColumns = [
    {
      title: 'REVISTA', key: 'title',
      render: (_, r) => (
        <div className="jcard-main">
          <div className="jcard-title">
            {(() => {
              const sjrUrl = ['https://', 'www.scimagojr.com'].join('') + '/journalsearch.php?q=' + (r.issn1 || '') + '&tip=isn';
              const doajUrl = r.issn1 ? ['https://', 'doaj.org/search/journals?q='].join('') + encodeURIComponent(r.issn1) : null;
              const linkUrl = doajUrl || sjrUrl;
              return IS_PUBLIC
                ? <a href={linkUrl} target="_blank" rel="noopener noreferrer"><Highlight text={r.title || '\u2014'} query={searchTitle} /><ArrowUpOutlined className="jcard-link-icon" /></a>
                : <a href="#" onClick={e => { e.preventDefault(); window.open(linkUrl, '_blank', 'noopener,noreferrer'); }}><Highlight text={r.title || '\u2014'} query={searchTitle} /><ArrowUpOutlined className="jcard-link-icon" /></a>;
            })()}
          </div>
          {r.categories && (
            <div className="jcard-cats">
              <span className="jcard-cat-label">Área:</span>
              <span className="jcard-cat-val">
                {r.categories.split(';')[0].replace(/\s*\(Q\d\)\s*$/, '').trim()}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'EDITORIAL', key: 'publisher', width: 220,
      render: (_, r) => {
        const name = r.publisher || '\u2014';
        const needsTooltip = name.length > 45;
        return (
          <div className="jcard-pub-section">
            {needsTooltip
              ? <Tooltip title={name} mouseEnterDelay={0.2}>
                  <div className="jcard-pub-name" style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                    {name}
                  </div>
                </Tooltip>
              : <div className="jcard-pub-name">{name}</div>
            }
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              <QuartileBadge label="Cuartil SJR" q={r.sjrq} />
          <QuartileBadge label="Cuartil JCR" q={r.jcrq} />
            </div>
          </div>
        );
      },
    },
    {
      title: 'ISSN', key: 'issn', width: 120,
      render: (_, r) => (
        <div className="jcard-apc-section">
          <Tooltip title="ISSN" mouseEnterDelay={0.3}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#007398', cursor: 'default' }}>
              {r.issn1 || r.issn2 || '\u2014'}
            </span>
          </Tooltip>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, marginTop: 6 }}>
            <div className="jcard-apc-label">Sin</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#27ae60', fontFamily: "'Inter','Segoe UI',sans-serif" }}>APC</div>
          </div>
        </div>
      ),
    },
  ];

  // ---- Render -------------------------------------------------------------

  const SJR_OPTIONS = [
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
  ];

  return (
    <div className="search-page" style={{ textAlign:'left' }}>

      {/* ── Info header (centrado) ── */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center', background: '#ffffff' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Revistas de acceso abierto sin cargos de publicación (APC)
        </div>
      </div>

      {/* ── Charts (estilo insights) ── */}
      {statsLoading ? (
        <div style={{ textAlign: 'center', padding: 24, background: '#ffffff' }}><Spin size="large" /></div>
      ) : (
        <div className="insights-row" style={{ background: '#ffffff', paddingBottom: 12, marginBottom: 0 }}>
          <div className="insights-stat-block">
            <div className="insights-number">
              {total > 0 ? total.toLocaleString('es-ES') : '—'}
            </div>
            <div className="insights-label">Revistas DOAJ</div>
          </div>
          <div className="insights-chart-card">
            <div className="insights-chart-title">Áreas temáticas</div>
            <ReactECharts option={makeAreaDonut('Áreas', areaChart)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} notMerge />
          </div>
          {jcrChart.length > 0 && (
          <div className="insights-chart-card">
            <div className="insights-chart-title">Cuartil JCR</div>
            <ReactECharts option={makeDonut('JCR', jcrChart)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} notMerge />
          </div>
          )}
          {sjrChart.length > 0 && (
          <div className="insights-chart-card">
            <div className="insights-chart-title">Cuartil SJR</div>
            <ReactECharts option={makeDonut('SJR', sjrChart)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} notMerge />
          </div>
          )}
        </div>
      )}

      {/* ── Search hero ── */}
      <div className="search-hero">
        <div className="search-modern-card">
          <div className="search-main-row">
            <Input
              size="large"
              placeholder="Buscar por título de revista…"
              value={searchTitle}
              onChange={e => {
                const val = e.target.value;
                setSearchTitle(val);
                clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                  setPage(1); setFilteredPage(1);
                  if (!sjrqFilter) {
                    fetchJournals(1, val, selPublisher, selSubject);
                  } else {
                    fetchFromBackend(1, val, selPublisher, selSubject, sjrqFilter, null);
                  }
                }, 450);
              }}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined style={{ color:'#aaa', fontSize:'18px' }} />}
              className="search-main-input"
              allowClear
            />
            <Button type="primary" size="large" onClick={handleSearch}
              icon={<SearchOutlined />} className="search-main-btn">
              Buscar
            </Button>
          </div>
          <div className="search-filters-grid" style={{ gridTemplateColumns:'repeat(3, 1fr)' }}>
            <div className="search-filter-item">
              <label className="search-filter-label">Editorial</label>
              <Select
                placeholder="Todas las editoriales"
                allowClear showSearch
                options={publisherOptions}
                value={selPublisher}
                onChange={v => {
                  const val = v || undefined;
                  setSelPublisher(val);
                  applyBackend(val, selSubject, sjrqFilter);
                }}
                style={{ width:'100%' }}
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 420, whiteSpace: 'normal' }}
                filterOption={(i,o) => o.label.toLowerCase().includes(i.toLowerCase())}
              />
            </div>
            <div className="search-filter-item">
              <label className="search-filter-label">Área DOAJ</label>
              <Select
                placeholder="Todas las áreas"
                allowClear showSearch
                options={subjectOptions}
                value={selSubject}
                onChange={v => {
                  const val = v || undefined;
                  setSelSubject(val);
                  applyBackend(selPublisher, val, sjrqFilter);
                }}
                style={{ width:'100%' }}
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 520, whiteSpace: 'normal' }}
                listHeight={320}
                filterOption={(i,o) => o.label.toLowerCase().includes(i.toLowerCase())}
              />
            </div>
            <div className="search-filter-item">
              <label className="search-filter-label">Cuartil SJR</label>
              <Select
                placeholder="Cualquier cuartil"
                allowClear
                options={SJR_OPTIONS}
                value={sjrqFilter}
                onChange={v => {
                  const val = v || null;
                  setSjrqFilter(val);
                  applyBackend(selPublisher, selSubject, val);
                }}
                style={{ width:'100%' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabla ── */}
      <div style={{ padding:'16px 48px 40px', maxWidth:1280, margin:'0 auto' }}>
        <div style={{ marginBottom:12, display:'flex', alignItems:'center' }}>
          {!loading && (
            <span className="journals-result-count">
              {(isBackend ? filteredTotal : resultTotal).toLocaleString('es-ES')} revistas encontradas
            </span>
          )}
        </div>
        <Table
          columns={isBackend ? scimagoColumns : columns}
          dataSource={visibleJournals}
          rowKey={isBackend ? 'sourceid' : 'id'}
          showHeader={false}
          loading={loading}
          className="custom-table journals-table"
          pagination={{
            current:  isBackend ? filteredPage : page,
            pageSize: PAGE_SIZE,
            total:    isBackend ? filteredTotal : Math.min(resultTotal, 1000),
            onChange: handlePageChange,
            showTotal: t => t.toLocaleString('es-ES') + ' resultados',
            showSizeChanger: false,
          }}
          locale={{ emptyText: loading ? 'Cargando…' : 'No se encontraron revistas' }}
        />
        <div style={{ marginTop:14, textAlign:'right' }}>
          <Text type="secondary" style={{ fontSize:11 }}>
            <GlobalOutlined style={{ marginRight:4 }} />
            <a href="https://doaj.org" target="_blank" rel="noopener noreferrer">DOAJ</a>
            {' · '}
            <a href="https://www.scimagojr.com" target="_blank" rel="noopener noreferrer">Scimago JR 2025</a>
            {' · '}
            <a href="https://jcr.clarivate.com" target="_blank" rel="noopener noreferrer">Clarivate JCR 2024</a>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default DiamondComponent;
